import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subject, take, takeUntil } from 'rxjs';
import { InfoReportService } from 'src/app/services/info-report.service';
import { LoginService } from 'src/app/services/login.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { AppUtils } from 'src/app/shared/app.utils';
import { AuthorizationByStatusModel } from 'src/app/shared/models/authorization-model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-info-report-detail',
  templateUrl: './info-report-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InfoReportDetailComponent implements OnInit, OnDestroy {
  @ViewChild('downloadTemplateLink', { static: true })
  displayErrorStatus: boolean = false;
  errorMessage: string = 'An error occurred.';
  infoReportDisplay: boolean = true;
  _infoReportLECaseNumbers: ServerModels.IRLECaseNumbersViewModel[] = [];
  _infoReportCrimeGunNumbers: ServerModels.IRCrimeGunViewModel[] = [];

  private _unsubscribe$: Subject<void> = new Subject<void>();

  private _infoReport: ServerModels.ENESS_IntelligenceReports;
  @Input() set infoReport(ir: ServerModels.ENESS_IntelligenceReports) {
    this._infoReport = ir;
    this.canEditIR(this._infoReport);
  }

  get infoReport(): ServerModels.ENESS_IntelligenceReports {
    return this._infoReport;
  }

  private _canEditIR: AuthorizationByStatusModel;
  get canEdit(): AuthorizationByStatusModel {
    this._canEditIR = AppPermissions.canEditIRs;
    return this._canEditIR;
  }

  set canEdit(authIR: AuthorizationByStatusModel) {
    this._canEditIR = authIR;
  }

  get infoReportLECaseNumbers(): ServerModels.IRLECaseNumbersViewModel[] {
    return this._infoReportLECaseNumbers;
  }

  set infoReportLECaseNumbers(value) {
    this._infoReportLECaseNumbers = value;
  }

  get infoReportCrimeGunNumbers(): ServerModels.IRCrimeGunViewModel[] {
    return this._infoReportCrimeGunNumbers;
  }

  set infoReportCrimeGunNumbers(value) {
    this._infoReportCrimeGunNumbers = value;
  }

  constructor(private infoReportService: InfoReportService,
    private router: Router,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    public previousRouteService: PreviousRouteService) { }

  ngOnInit() {
    if (this.infoReport) {
      forkJoin({
        ir:this.infoReportService.getIRLECaseNumbersByIRID(this.infoReport.IntelligenceReportID),
        irCrimeGuns:this.infoReportService.getCrimeGunViewModelByIRID(this.infoReport.IntelligenceReportID)
      }).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (val: { ir: ServerModels.IRLECaseNumbersViewModel[], irCrimeGuns: ServerModels.IRCrimeGunViewModel[] }) => {
          this.infoReportLECaseNumbers = val.ir;
          this.infoReportCrimeGunNumbers = val.irCrimeGuns;
          this.cdRef.detectChanges();
        }
      });
    } else if (!!this.route.snapshot.params['id'] && (this.route.component instanceof InfoReportDetailComponent || this.route.component['name'] === 'InfoReportDetailComponent')) {
      const id = this.route.snapshot.params['id'];
      forkJoin({
        ir:this.infoReportService.getInformationReportById(id),
        leCaseNumbers:this.infoReportService.getIRLECaseNumbersByIRID(id),
        irCrimeGuns:this.infoReportService.getCrimeGunViewModelByIRID(id)
      }).pipe(take(1)).subscribe({
        next: (val: { ir: ServerModels.ENESS_IntelligenceReports, leCaseNumbers: ServerModels.IRLECaseNumbersViewModel[], irCrimeGuns: ServerModels.IRCrimeGunViewModel[] }) => {
          this.infoReport = val.ir;
          this.canEditIR(this.infoReport);
          this.infoReportLECaseNumbers = val.leCaseNumbers;
          this.infoReportCrimeGunNumbers = val.irCrimeGuns;
          this.cdRef.detectChanges();
        }
      });
    }
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private canEditIR(ir: ServerModels.ENESS_IntelligenceReports): AuthorizationByStatusModel {
    if (this.canEdit) {
      let fieldDivision: ServerModels.ENESS_REF_FieldDivisions = LoginService.fieldDivisionsSubj.getValue().find((fd: ServerModels.ENESS_REF_FieldDivisions) => fd.ENESS_REF_NIBINCGICCodes?.OrganizationCode == ir.CGICCode);
      this.canEdit.authOrgCode = fieldDivision?.FieldDivisionID;
      this.canEdit.creatorId = ir.CreatedBy;
      this.canEdit.assigneeIds = ir.ENESS_XREF_IRsAssignedCGICPersonnel?.map(x => x.UserID);
    }
    return this._canEditIR;
  }


  public onClickDownload(file: ServerModels.ENESS_IntelligenceReportAttachments) {
    if (file) {
      this.errorMessage = '';
      this.displayErrorStatus = false;
      this.infoReportService.getAttachmentById(this.infoReport.IntelligenceReportID, file.IntelligenceReportAttachmentID).subscribe(
        (attachment:Blob) => {
          AppUtils.embedDownloadLink(attachment, file.FileName);
        },
        (err:any) => {
          this.errorMessage = `${err} Unable to retrieve requested attachment.`;
          this.displayErrorStatus = true;
        }
      );
    }
  }

  public editIR(): void {
    if (this.route.component['name'] == 'CrimeGunDetailComponent') {
      this.previousRouteService.returnUrl = this.router.url;
    } else {
      this.previousRouteService.returnUrl = null;
    }
    this.router.navigate(['/info-report-form', this.infoReport.IntelligenceReportID]);
  }

  public openRelatedCrimeGun(evt: Event, id: string): void {
    evt.preventDefault();
    const token = sessionStorage.getItem(environment.authTokenStorageKey);
    let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id;
    let windowObjRef = window.open(url, '_blank');
    windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
  }

}
